// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

export interface ComposeEventOptions {
  readonly stopPropagation?: boolean;
  readonly preventDefault?: boolean;
}

export interface EventLike {
  preventDefault(): void;
  stopPropagation(): void;
}

const applyOptions = (e: EventLike, options: ComposeEventOptions): void => {
  if (options.stopPropagation) e.stopPropagation();
  if (options.preventDefault) e.preventDefault();
};

/**
 * Composes an event handler with base event behaviors (stopPropagation,
 * preventDefault). Works with DOM Event, React SyntheticEvent, or any event-like
 * object.
 *
 * @example
 *
 * ```ts
 * const handler = (e: MouseEvent) => doSomething(e);
 * const onHandler = composeEventHandler(handler, {
 *   stopPropagation: true,
 *   preventDefault: true,
 * });
 * onHandler(event);
 * ```
 */
export const composeEventHandler =
  <E extends EventLike, R = void>(
    handler: (e: E) => R,
    options: ComposeEventOptions = {},
  ): ((e: E) => R) =>
  (e: E) => {
    applyOptions(e, options);
    return handler(e);
  };
